import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import greenCheckmark from '../../assets/icons/green-checkmark-circled.svg';
import { Loader } from '../../components/Loader/Loader';
import { AccountInfoModal, CompanyInfoModal, DealerInfoModal, SecurityModal, PasswordModal } from '../../components/ProfileEditModal';
import { useUserProfile, useUpdateAccountInfo, useUpdateCompanyInfo, useUpdateDealerInfo, useUpdateUsername, useUpdatePassword } from '../../hooks/useUserProfile';
import { useMvpPartners } from '../../hooks/useMvpPartners';
import { MvpPartner, MvpPartnerContact } from '../../types/mvpPartners';
import { AccountInformation, CompanyInformation, DealerInformation } from '../../types/user.types';

import './Profile.scss';

export const Profile: React.FC = () => {
  const { t } = useTranslation('profile');

  const { data: profileData, isLoading } = useUserProfile();
  const { data: mvpPartnersData = [] } = useMvpPartners();

  // Modal states
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [dealerModalOpen, setDealerModalOpen] = useState(false);

  const [securityModalOpen, setSecurityModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  
  // Success message state
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  

  
  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showErrorMessage = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 3000);
  };

  const updateAccountInfo = useUpdateAccountInfo();
  const handleAccountUpdate = async (data: Record<string, any>) => {
    try {

      // Preserve read-only fields from existing account information
      const existingAccountInfo = profileData?.accountInformations || {} as AccountInformation;
      
      // Update account information
      const accountUpdatePayload: Partial<AccountInformation> = {
        // Preserve read-only fields
        membershipNumber: existingAccountInfo.membershipNumber,
        memberStatus: existingAccountInfo.memberStatus,
        memberType: existingAccountInfo.memberType,
        enrollmentDate: existingAccountInfo.enrollmentDate,
        // Update editable fields from form
        professionalEmail: data.professionalEmail || undefined,
        personalEmail: data.personalEmail || undefined,
        profession: data.profession || undefined,
        // Always include URL fields, even if empty
        websitePortfolio: data.websitePortfolio?.trim() || '',
        instagramUrl: data.instagramUrl?.trim() || '',
        linkedinUrl: data.linkedinUrl?.trim() || ''
      };
      
      // Update account info
      await updateAccountInfo.mutateAsync(accountUpdatePayload);
      
      showSuccessMessage(t('sections.account.updateSuccess'));
      setAccountModalOpen(false);
    } catch (error) {
      console.error('Error updating account info:', error);
      showErrorMessage(t('profile:updateError'));
    }
  };

  const updateCompanyInfo = useUpdateCompanyInfo();
  const handleCompanyUpdate = async (data: Record<string, any>) => {
    try {

      const companyInfo: Partial<CompanyInformation> = {
        companyName: data.companyName,
        companyStreet: data.address,
        companyCity: data.city,
        companyState: data.province,
        companyPostalCode: data.postalCode,
        companyPhone: data.professionalPhone || '',
        companyUnit: data.unit || '',
        companyCountry: 'CA' // Always set to Canada
      };
    
      
      await updateCompanyInfo.mutateAsync(companyInfo);
      showSuccessMessage(t('sections.company.updateSuccess'));
      setCompanyModalOpen(false);
    } catch (error) {
      console.error('Error updating company info:', error);
      showErrorMessage(t('sections.messages.updateError'));
    }
  };

  const updateDealerInfo = useUpdateDealerInfo();
  const handleDealerUpdate = async (data: Record<string, any>) => {
    try {
      // The backend expects numeric IDs, but we're getting Salesforce IDs from the form
      // We need to extract numeric IDs from the MVP partners data
      
      // Find the selected company in the MVP partners data
      const selectedCompany = mvpPartnersData.find(
        (partner: MvpPartner) => partner.remoteObjectId === data.dealerCompanyId || 
                 partner.remoteObjectId === data.dealerCompanyRemoteId
      );
      
      // Find the selected representative in the contacts
      const selectedRep = mvpPartnersData.flatMap((partner: MvpPartner) => partner.contacts || [])
        .find((contact: MvpPartnerContact) => 
          contact.remoteObjectId === data.dealerRepresentativeId || 
          contact.remoteObjectId === data.dealerRepresentativeRemoteId
        );
      
      // Convert form data to DealerInformation format
      const dealerInfo: Partial<DealerInformation> = {
        primaryDealer: data.primaryDealer || '',
        dealerRepresentative: data.dealerRepresentative || '',
        // Use the numeric IDs from the found objects
        ...(selectedCompany?.id && { dealerCompanyId: selectedCompany.id }),
        ...(selectedRep?.id && { dealerRepresentativeId: selectedRep.id }),
        // Include remote IDs as strings
        dealerCompanyRemoteId: data.dealerCompanyRemoteId || '',
        dealerRepresentativeRemoteId: data.dealerRepresentativeRemoteId || ''
      };
            
      // Call mutation to update dealer info
      await updateDealerInfo.mutateAsync(dealerInfo);
      
      showSuccessMessage(t('sections.messages.dealerInfoUpdated'));
      setDealerModalOpen(false);
    } catch (error) {
      console.error('Error updating dealer info:', error);
      showErrorMessage(t('sections.messages.updateError'));
    }
  };



  const updatePassword = useUpdatePassword();
  const handlePasswordUpdate = async (data: Record<string, any>) => {
    try {
      await updatePassword.mutateAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });
      showSuccessMessage(t('sections.messages.passwordUpdated'));
      setPasswordModalOpen(false);
    } catch (error) {
      console.error('Error updating password:', error);
      showErrorMessage(t('sections.messages.updateError'));
    }
  };

  const updateUsername = useUpdateUsername();
  const handleSecurityUpdate = async (data: Record<string, any>) => {
    try {
      await updateUsername.mutateAsync(data.newUsername);
      showSuccessMessage(t('sections.messages.securityUpdated'));
      setSecurityModalOpen(false);
    } catch (error) {
      console.error('Error updating security info:', error);
      showErrorMessage(t('sections.messages.updateError'));
    }
  };

  if (isLoading) {
    return (
      <div className="profile">
        <Loader variant="fullscreen" />
      </div>
    );
  }

  return (
    <div className="profile">
      <h1 className="profile__title">{t('title')}</h1>
      <p className="profile__subtitle">{t('subtitle')}</p>

      <div className="profile__grid">
        {/* Account Information Section */}
        <section className="profile-section">
          <div className="profile-section__header">
            <h2 className="profile-section__title">{t('sections.account.title')}</h2>
            <button 
              className="profile-section__edit" 
              onClick={() => setAccountModalOpen(true)}
            >
              {t('sections.actions.edit')}
            </button>
          </div>
          <div className="profile-section__content">
            <div className="profile-section__field">
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.account.firstName')}</label>
                  <p>{profileData?.firstName || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.account.lastName')}</label>
                  <p>{profileData?.lastName || ''}</p>
                </div>
              </div>
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.account.professionalEmail')}</label>
                  <p>{profileData?.accountInformations?.professionalEmail || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.account.personalEmail')}</label>
                  <p>{profileData?.accountInformations?.personalEmail || ''}</p>
                </div>
              </div>
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.account.websitePortfolio')}</label>
                  <p>{profileData?.accountInformations?.websitePortfolio || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.account.instagramProfile')}</label>
                  <p>{profileData?.accountInformations?.instagramUrl || ''}</p>
                </div>
              </div>
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.account.linkedinProfile')}</label>
                  <p>{profileData?.accountInformations?.linkedinUrl || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.account.profession')}</label>
                  <p>{profileData?.accountInformations?.profession || ''}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Information Section */}
        <section className="profile-section">
          <div className="profile-section__header">
            <h2 className="profile-section__title">{t('sections.company.title')}</h2>
            <button 
              className="profile-section__edit" 
              onClick={() => setCompanyModalOpen(true)}
            >
              {t('sections.actions.edit')}
            </button>
          </div>
          <div className="profile-section__content">
            <div className="profile-section__field">
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.company.companyName')}</label>
                  <p>{profileData?.companyInformations?.companyName || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.company.professionalPhone')}</label>
                  <p>{profileData?.companyInformations?.companyPhone || ''}</p>
                </div>
              </div>
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.company.address')}</label>
                  <p>{profileData?.companyInformations?.companyStreet || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.company.unit')}</label>
                  <p>{profileData?.companyInformations?.companyUnit || ''}</p>
                </div>
              </div>
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.company.postalCode')}</label>
                  <p>{profileData?.companyInformations?.companyPostalCode || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.company.city')}</label>
                  <p>{profileData?.companyInformations?.companyCity || ''}</p>
                </div>
              </div>
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.company.province')}</label>
                  <p>{profileData?.companyInformations?.companyState || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.company.country')}</label>
                  <p>{profileData?.companyInformations?.companyCountry === 'CA' ? 'Canada' : (profileData?.companyInformations?.companyCountry || '')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dealer Information Section */}
        <section className="profile-section">
          <div className="profile-section__header">
            <h2 className="profile-section__title">{t('sections.dealer.title')}</h2>
            <button 
              className="profile-section__edit" 
              onClick={() => setDealerModalOpen(true)}
            >
              {t('sections.actions.edit')}
            </button>
          </div>
          <div className="profile-section__content">
            <div className="profile-section__field">
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <label>{t('sections.dealer.primaryDealer')}</label>
                  <p>{profileData?.dealerInformations?.primaryDealer || ''}</p>
                </div>
                <div className="profile-section__column">
                  <label>{t('sections.dealer.salesRepresentative')}</label>
                  <p>{profileData?.dealerInformations?.dealerRepresentative || ''}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Security Information Section */}
        <section className="profile-section security-section">
          <div className="profile-section__header">
            <h2 className="profile-section__title">{t('sections.security.title')}</h2>
          </div>
          <div className="profile-section__content">
            <div className="profile-section__field">
              <div className="profile-section__row">
                <div className="profile-section__column">
                  <div className="profile-section__label-group">
                    <label>{t('sections.security.username')}</label>
                    <button 
                      className="profile-section__edit-link" 
                      onClick={() => setSecurityModalOpen(true)}
                    >
                      {t('sections.actions.edit')}
                    </button>
                  </div>
                  <p className="profile-section__value">{profileData?.securityInformations?.username || ''}</p>
                </div>
                <div className="profile-section__column">
                  <div className="profile-section__label-group">
                    <label>{t('sections.security.password')}</label>
                    <button 
                      className="profile-section__edit-link" 
                      onClick={() => setPasswordModalOpen(true)}
                    >
                      {t('sections.actions.edit')}
                    </button>
                  </div>
                  <p className="profile-section__value">{'*********'}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Success Message */}
      {successMessage && (
        <div className="profile__success-message">
          <img src={greenCheckmark} alt="success" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="profile__error-message">
          {errorMessage}
        </div>
      )}

      {/* Modals */}
      <PasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onSuccess={handlePasswordUpdate}
      />
      <AccountInfoModal
        isOpen={accountModalOpen}
        onClose={() => setAccountModalOpen(false)}
        onSuccess={async (data) => {
          await handleAccountUpdate(data);
          showSuccessMessage(t('sections.messages.accountUpdated'));
        }}
        initialData={{
          firstName: profileData?.firstName || '',
          lastName: profileData?.lastName || '',
          accountInformations: {
            professionalEmail: profileData?.accountInformations?.professionalEmail || '',
            personalEmail: profileData?.accountInformations?.personalEmail || '',
            websitePortfolio: profileData?.accountInformations?.websitePortfolio || '',
            instagramUrl: profileData?.accountInformations?.instagramUrl || '',
            linkedinUrl: profileData?.accountInformations?.linkedinUrl || '',
            profession: profileData?.accountInformations?.profession || ''
          }
        }}
      />

      <CompanyInfoModal
        isOpen={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        onSuccess={async (data) => {
          await handleCompanyUpdate(data);
          showSuccessMessage(t('sections.messages.companyUpdated'));
        }}
        initialData={{
          // Map API fields to form fields
          companyName: profileData?.companyInformations?.companyName || '',
          professionalPhone: profileData?.companyInformations?.companyPhone || '',
          address: profileData?.companyInformations?.companyStreet || '',
          unit: profileData?.companyInformations?.companyUnit || '',
          postalCode: profileData?.companyInformations?.companyPostalCode || '',
          city: profileData?.companyInformations?.companyCity || '',
          province: profileData?.companyInformations?.companyState || '',
          companyCountry: profileData?.companyInformations?.companyCountry || ''
        }}
      />

      <DealerInfoModal
        isOpen={dealerModalOpen}
        onClose={() => setDealerModalOpen(false)}
        onSuccess={handleDealerUpdate}
        initialData={{
          primaryDealer: profileData?.dealerInformations?.primaryDealer || '',
          dealerRepresentative: profileData?.dealerInformations?.dealerRepresentative || ''
        }}
      />

      <SecurityModal
        isOpen={securityModalOpen}
        onClose={() => setSecurityModalOpen(false)}
        onSuccess={async (data) => {
          await handleSecurityUpdate(data);
          showSuccessMessage(t('sections.messages.securityUpdated'));
        }}
        initialData={{
          currentUsername: profileData?.securityInformations?.username || ''
        }}
      />
    </div>
  );
};
